import React, { useEffect, useState, useCallback } from 'react';
import { useToast, useConfirm } from '../context/UIContext';
import { useLocation, useParams, Link } from 'react-router-dom';
import { getCurrentUserId, getCurrentUserRole } from '../utils/authHelper';
import {
    getDailyWorkRecords,
    createDailyWorkRecord,
    deleteDailyWorkRecord,
    closeDailyWorkRecord,
    reopenDailyWorkRecord,
    updateDailyWorkRecord,
} from '../services/DailyWorkRecordService';
import {
    getAllocationsForRecord,
    createAllocation,
    updateAllocation,
    deleteAllocation,
} from '../services/ProjectAllocationService';
import { getTicketsByProject } from '../services/ProjectTicketService';
import TimeClock from '../components/TimeClock';
import api from '../services/api';
import { extractApiError } from '../utils/apiError';
import { formatDate, weekdayName } from '../utils/dateConverter';
import '../styles/DailyTimeTracking.css';

function fmtMinutes(minutes) {
    const h = Math.floor(Math.abs(minutes) / 60);
    const m = Math.abs(minutes) % 60;
    const sign = minutes < 0 ? '-' : '';
    return `${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function parseHHMM(str) {
    if (!str) return 0;
    const parts = str.split(':');
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
}

function today() {
    return new Date().toISOString().split('T')[0];
}

const EMPTY_FORM = { date: today(), start_time: '', end_time: '', break_minutes: 0, travel_minutes: 0 };

// ArbZG §4: attendance span > 9 h requires a 45 min break, > 6 h requires 30 min.
// Mirrors the backend enforcement so the user gets instant feedback.
const legalMinBreak = (startStr, endStr) => {
    if (!startStr || !endStr) return 0;
    const [sh, sm] = startStr.split(':').map(Number);
    const [eh, em] = endStr.split(':').map(Number);
    const gross = (eh * 60 + em) - (sh * 60 + sm);
    if (gross > 9 * 60) return 45;
    if (gross > 6 * 60) return 30;
    return 0;
};
const EMPTY_ALLOC = { project_id: '', ticket_id: '', allocated_time: '', travel_minutes: 0, activity: '', comment: '', location: 'Office' };
// Values MUST match the backend location_type enum ('Home Office' | 'Office' | 'On-Site').
const LOCATION_OPTIONS = [
    { value: 'Office',      label: 'Büro'       },
    { value: 'Home Office', label: 'Homeoffice' },
    { value: 'On-Site',     label: 'Vor Ort'    },
];
const locationLabel = (v) => LOCATION_OPTIONS.find(o => o.value === v)?.label || v || '—';

export default function DailyTimeTracking() {
    const toast = useToast();
    const confirm = useConfirm();
    const location = useLocation();
    // /timetracking/:userId edits that employee's days. The route is behind AdminRoute
    // and the API refuses a non-admin anyway; this only decides what the page asks for.
    const { userId: routeUserId } = useParams();
    const ownId = getCurrentUserId();
    const isAdmin = ['admin', 'super_admin'].includes(getCurrentUserRole());
    const adminMode = Boolean(routeUserId) && routeUserId !== ownId && isAdmin;
    const userId = adminMode ? routeUserId : ownId;
    const [targetName, setTargetName] = useState('');

    const queryParams = new URLSearchParams(location.search);
    const paramFilter = queryParams.get('filter');
    const initialFilter = ['all', 'open', 'closed'].includes(paramFilter) ? paramFilter : 'open';

    const [records, setRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filter, setFilter] = useState(initialFilter);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formError, setFormError] = useState('');
    const [projects, setProjects] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [allocations, setAllocations] = useState({}); // { recordId: [] }
    const [allocForm, setAllocForm] = useState(EMPTY_ALLOC);
    const [allocError, setAllocError] = useState({});
    const [closeError, setCloseError] = useState({});
    const [editingAllocId, setEditingAllocId] = useState(null);
    const [editAllocForm, setEditAllocForm] = useState({});
    const [ticketsByProject, setTicketsByProject] = useState({});
    const [editingRecordId, setEditingRecordId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [showAllRecords, setShowAllRecords] = useState(false);

    const loadRecords = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await getDailyWorkRecords(null, adminMode ? userId : null);
            setRecords(data);
        } catch (err) {
            toast(extractApiError(err, 'Fehler beim Laden der Einträge.'));
        } finally {
            setIsLoading(false);
        }
    }, [adminMode, userId]);

    const loadProjects = useCallback(async () => {
        if (!userId) return;
        try {
            const res = await api.get(`/User/${userId}/Projects`);
            setProjects(res.data.projects || []);
        } catch {
            setProjects([]);
        }
    }, [userId]);

    useEffect(() => {
        loadRecords();
        loadProjects();
    }, [loadRecords, loadProjects]);

    // For the banner: without it it can only show a UUID.
    useEffect(() => {
        if (!adminMode) { setTargetName(''); return; }
        let cancelled = false;
        api.get(`/User/${userId}`)
            .then(r => { if (!cancelled) setTargetName(r.data?.name || r.data?.username || ''); })
            .catch(() => { if (!cancelled) setTargetName(''); });
        return () => { cancelled = true; };
    }, [adminMode, userId]);

    const loadAllocations = async (recordId) => {
        try {
            const data = await getAllocationsForRecord(recordId);
            setAllocations(prev => ({ ...prev, [recordId]: data }));
        } catch {
            setAllocations(prev => ({ ...prev, [recordId]: [] }));
        }
    };

    const toggleExpand = (recordId) => {
        if (expandedId === recordId) {
            setExpandedId(null);
        } else {
            setExpandedId(recordId);
            setAllocForm(EMPTY_ALLOC);
            setAllocError(prev => ({ ...prev, [recordId]: '' }));
            setCloseError(prev => ({ ...prev, [recordId]: '' }));
            if (!allocations[recordId]) {
                loadAllocations(recordId);
            }
        }
    };

    const handleFormChange = (e) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmitRecord = async (e) => {
        e.preventDefault();
        setFormError('');
        if (form.start_time && form.end_time && form.end_time <= form.start_time) {
            setFormError('Die Endzeit muss nach der Startzeit liegen.');
            return;
        }
        const minBreak = legalMinBreak(form.start_time, form.end_time);
        if ((parseInt(form.break_minutes) || 0) < minBreak) {
            setFormError(`Bei dieser Arbeitszeit ist laut ArbZG §4 eine Pause von mindestens ${minBreak} Minuten erforderlich.`);
            return;
        }
        try {
            await createDailyWorkRecord({
                date: form.date,
                start_time: form.start_time,
                end_time: form.end_time,
                break_minutes: parseInt(form.break_minutes) || 0,
                travel_minutes: parseInt(form.travel_minutes) || 0,
                // Admin mode only: otherwise the API takes the owner from the token.
                ...(adminMode ? { user_id: userId } : {}),
            });
            setForm(EMPTY_FORM);
            await loadRecords();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setFormError(typeof detail === 'string' ? detail : 'Fehler beim Speichern.');
        }
    };

    const handleDeleteRecord = async (id) => {
        // The record's allocations go with it (ON DELETE CASCADE), so the confirm has to
        // name them — deleting a day removes those hours from the project statistics too.
        const count = (allocations[id] || []).length;
        const message = count > 0
            ? `Eintrag wirklich löschen? ${count} Projektzuweisung${count === 1 ? '' : 'en'} werden mit gelöscht.`
            : 'Eintrag wirklich löschen? Zugehörige Projektzuweisungen werden mit gelöscht.';
        if (!await confirm(message, { confirmLabel: 'Löschen', danger: true })) return;
        try {
            await deleteDailyWorkRecord(id);
            await loadRecords();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast(typeof detail === 'string' ? detail : 'Löschen fehlgeschlagen.');
        }
    };

    const handleStartEdit = (record) => {
        setEditingRecordId(record.id);
        setEditForm({
            start_time: record.start_time,
            end_time: record.end_time,
            break_minutes: record.break_minutes,
            travel_minutes: record.travel_minutes,
        });
    };

    const handleSaveEdit = async (recordId) => {
        const minBreak = legalMinBreak(editForm.start_time, editForm.end_time);
        if ((parseInt(editForm.break_minutes) || 0) < minBreak) {
            toast(`Bei dieser Arbeitszeit ist laut ArbZG §4 eine Pause von mindestens ${minBreak} Minuten erforderlich.`);
            return;
        }
        try {
            await updateDailyWorkRecord(recordId, {
                start_time: editForm.start_time,
                end_time: editForm.end_time,
                break_minutes: parseInt(editForm.break_minutes) || 0,
                travel_minutes: parseInt(editForm.travel_minutes) || 0,
            });
            setEditingRecordId(null);
            await loadRecords();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast(typeof detail === 'string' ? detail : 'Aktualisierung fehlgeschlagen.');
        }
    };

    const handleAddAllocation = async (record) => {
        setAllocError(prev => ({ ...prev, [record.id]: '' }));
        if (!allocForm.project_id) {
            setAllocError(prev => ({ ...prev, [record.id]: 'Bitte Projekt auswählen.' }));
            return;
        }
        const allocMins = parseHHMM(allocForm.allocated_time);
        if (!allocMins || allocMins <= 0) {
            setAllocError(prev => ({ ...prev, [record.id]: 'Zeit muss größer als 00:00 sein.' }));
            return;
        }
        // Over-allocation is allowed: a day may be attributed to several projects beyond the
        // recorded work time. The counted work time stays the record's NetWorkMinutes.
        try {
            await createAllocation({
                daily_work_record_id: record.id,
                project_id: allocForm.project_id,
                ticket_id: allocForm.ticket_id || null,
                allocated_minutes: allocMins,
                travel_minutes: parseInt(allocForm.travel_minutes) || 0,
                activity: allocForm.activity || null,
                comment: allocForm.comment || null,
                location: allocForm.location || null,
            });
            setAllocForm(EMPTY_ALLOC);
            await loadAllocations(record.id);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setAllocError(prev => ({ ...prev, [record.id]: typeof detail === 'string' ? detail : 'Fehler.' }));
        }
    };

    const handleDeleteAllocation = async (allocId, recordId) => {
        if (!await confirm('Projektzuweisung wirklich löschen?', { confirmLabel: 'Löschen', danger: true })) return;
        try {
            await deleteAllocation(allocId);
            await loadAllocations(recordId);
        } catch (err) {
            toast(extractApiError(err, 'Löschen der Zuweisung fehlgeschlagen.'));
        }
    };

    const handleStartEditAlloc = (alloc) => {
        setEditingAllocId(alloc.id);
        setEditAllocForm({
            allocated_time: fmtMinutes(alloc.allocated_minutes),
            travel_minutes: alloc.travel_minutes,
            activity: alloc.activity || '',
            comment: alloc.comment || '',
            location: alloc.location || '',
            ticket_id: alloc.ticket_id || '',
        });
        // This project's tickets are fetched lazily when a project is picked in the add form, so
        // a page reloaded straight into editing has nothing cached.
        if (alloc.project_id) loadTicketsForProject(alloc.project_id);
    };

    const handleSaveEditAlloc = async (allocId, recordId) => {
        const mins = parseHHMM(editAllocForm.allocated_time);
        if (!mins || mins <= 0) {
            toast('Zeit muss größer als 00:00 sein.');
            return;
        }
        try {
            await updateAllocation(allocId, {
                allocated_minutes: mins,
                travel_minutes: parseInt(editAllocForm.travel_minutes) || 0,
                activity: editAllocForm.activity || null,
                comment: editAllocForm.comment || null,
                location: editAllocForm.location || null,
                // Always sent: null is what removes an assigned ticket again.
                ticket_id: editAllocForm.ticket_id || null,
            });
            setEditingAllocId(null);
            await loadAllocations(recordId);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast(typeof detail === 'string' ? detail : 'Speichern fehlgeschlagen.');
        }
    };

    const handleCancelEditAlloc = () => setEditingAllocId(null);

    const loadTicketsForProject = async (projectId) => {
        if (!projectId || ticketsByProject[projectId]) return;
        try {
            const data = await getTicketsByProject(projectId);
            setTicketsByProject(prev => ({ ...prev, [projectId]: data }));
        } catch {
            setTicketsByProject(prev => ({ ...prev, [projectId]: [] }));
        }
    };

    const handleReopenDay = async (recordId) => {
        try {
            await reopenDailyWorkRecord(recordId);
            await loadRecords();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast(typeof detail === 'string' ? detail : 'Tag konnte nicht wieder geöffnet werden.');
        }
    };

    const handleCloseDay = async (recordId) => {
        setCloseError(prev => ({ ...prev, [recordId]: '' }));
        try {
            await closeDailyWorkRecord(recordId);
            await loadRecords();
            setExpandedId(null);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setCloseError(prev => ({
                ...prev,
                [recordId]: typeof detail === 'string' ? detail : 'Tag konnte nicht abgeschlossen werden.',
            }));
        }
    };

    const filteredRecords = records
        .filter(r => {
            if (filter === 'open') return r.status === 'open';
            if (filter === 'closed') return r.status === 'closed';
            return true;
        })
        .sort((a, b) => b.date.localeCompare(a.date));

    const PAGE_SIZE = 20;
    const displayedRecords = showAllRecords ? filteredRecords : filteredRecords.slice(0, PAGE_SIZE);

    return (
        <div className="daily-tracking-page">
            <h2>{adminMode ? 'Zeiterfassung bearbeiten' : 'Zeiterfassung'}</h2>

            {adminMode ? (
                /* Every control below writes to someone else's account. */
                <div className="admin-mode-banner" role="status">
                    <span className="admin-mode-banner-label">Fremde Zeiterfassung</span>
                    <span className="admin-mode-banner-text">
                        Du bearbeitest die Einträge von <strong>{targetName || userId}</strong>.
                        Änderungen werden im Protokoll deinem Konto zugeordnet.
                    </span>
                    <Link className="admin-mode-banner-back" to={`/employee-hours/${userId}`}>
                        Zur Übersicht
                    </Link>
                </div>
            ) : (
                /* Booking a day with the clock writes the same record as the form, so the list
                   reloads when it stops. Hidden in admin mode: the clock is self-only. */
                <TimeClock onBooked={loadRecords} />
            )}

            <section className="record-form-section">
                <h3>
                    {adminMode
                        ? `Arbeitstag für ${targetName || 'Mitarbeiter'} nachtragen`
                        : 'Neuen Arbeitstag erfassen'}
                </h3>
                <form className="record-form" onSubmit={handleSubmitRecord}>
                    <div className="form-row">
                        <label>
                            Datum{form.date ? ` · ${weekdayName(form.date)}` : ''}
                            <input
                                type="date"
                                name="date"
                                value={form.date}
                                max={today()}
                                onChange={handleFormChange}
                                required
                            />
                        </label>
                        <label>
                            Start
                            <input
                                type="time"
                                name="start_time"
                                value={form.start_time}
                                onChange={handleFormChange}
                                required
                            />
                        </label>
                        <label>
                            Ende
                            <input
                                type="time"
                                name="end_time"
                                value={form.end_time}
                                onChange={handleFormChange}
                                required
                            />
                        </label>
                        <label>
                            Pause (min)
                            <input
                                type="number"
                                name="break_minutes"
                                value={form.break_minutes}
                                min="0"
                                step="1"
                                onChange={handleFormChange}
                            />
                        </label>
                        <label>
                            Fahrtzeit (min)
                            <input
                                type="number"
                                name="travel_minutes"
                                value={form.travel_minutes}
                                min="0"
                                step="1"
                                onChange={handleFormChange}
                            />
                        </label>
                        <button type="submit" className="button">Eintrag speichern</button>
                    </div>
                    {formError && <p className="error-text">{formError}</p>}
                </form>
            </section>

            <div className="filter-bar">
                {['all', 'open', 'closed'].map(f => (
                    <button
                        key={f}
                        className={`button ${filter === f ? 'active-filter' : ''}`}
                        onClick={() => setFilter(f)}
                    >
                        {f === 'all' ? 'Alle' : f === 'open' ? 'Offen' : 'Abgeschlossen'}
                    </button>
                ))}
            </div>

            <div className="records-list">
                {isLoading ? (
                    <p className="empty-msg">Daten werden geladen…</p>
                ) : filteredRecords.length === 0 ? (
                    <p className="empty-msg">Keine Einträge gefunden.</p>
                ) : null}
                {!isLoading && displayedRecords.map(record => {
                    const recAllocs = allocations[record.id] || [];
                    const allocatedMin = recAllocs.reduce((s, a) => s + a.allocated_minutes, 0);
                    const remainingMin = record.net_work_minutes - allocatedMin;
                    const allocTravelMin = recAllocs.reduce((s, a) => s + a.travel_minutes, 0);
                    const isExpanded = expandedId === record.id;
                    const isEditing = editingRecordId === record.id;

                    return (
                        <div key={record.id} className={`record-card ${record.status}`}>
                            <div
                                className="record-header"
                                onClick={() => record.status === 'open' && toggleExpand(record.id)}
                            >
                                <div className="record-meta">
                                    <span className="record-date">
                                        {formatDate(record.date)}
                                        <span className="record-weekday">{weekdayName(record.date)}</span>
                                    </span>
                                    <span className="record-times">{record.start_time} &ndash; {record.end_time}</span>
                                    <span>Pause: {record.break_minutes} min</span>
                                    {record.travel_minutes > 0 && (
                                        <span>Fahrt: {record.travel_minutes} min</span>
                                    )}
                                    <span className="record-net">
                                        {record.travel_minutes > 0 ? (
                                            <strong>
                                                Gesamt: {fmtMinutes(record.net_work_minutes + record.travel_minutes)}
                                                <span className="record-net-detail"> ({fmtMinutes(record.net_work_minutes)} + {record.travel_minutes} min Fahrt)</span>
                                            </strong>
                                        ) : (
                                            <strong>Nettozeit: {fmtMinutes(record.net_work_minutes)}</strong>
                                        )}
                                    </span>
                                </div>
                                <span className={`status-badge status-${record.status}`}>
                                    {record.status === 'open' ? 'Offen' : 'Abgeschlossen'}
                                </span>
                            </div>

                            {record.status === 'open' && !isEditing && (
                                <div className="record-actions">
                                    <button
                                        className="button btn-sm"
                                        onClick={(e) => { e.stopPropagation(); handleStartEdit(record); }}
                                    >
                                        Bearbeiten
                                    </button>
                                    <button
                                        className="button button-danger btn-sm"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteRecord(record.id); }}
                                    >
                                        Löschen
                                    </button>
                                </div>
                            )}

                            {isEditing && (
                                <div className="edit-form">
                                    <label>
                                        Start
                                        <input
                                            type="time"
                                            value={editForm.start_time}
                                            onChange={e => setEditForm(p => ({ ...p, start_time: e.target.value }))}
                                        />
                                    </label>
                                    <label>
                                        Ende
                                        <input
                                            type="time"
                                            value={editForm.end_time}
                                            onChange={e => setEditForm(p => ({ ...p, end_time: e.target.value }))}
                                        />
                                    </label>
                                    <label>
                                        Pause
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={editForm.break_minutes}
                                            onChange={e => setEditForm(p => ({ ...p, break_minutes: e.target.value }))}
                                        />
                                    </label>
                                    <label>
                                        Fahrtzeit
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={editForm.travel_minutes}
                                            onChange={e => setEditForm(p => ({ ...p, travel_minutes: e.target.value }))}
                                        />
                                    </label>
                                    <button className="button" onClick={() => handleSaveEdit(record.id)}>Speichern</button>
                                    <button className="button" onClick={() => setEditingRecordId(null)}>Abbrechen</button>
                                </div>
                            )}

                            {record.status === 'closed' && (
                                <div className="closed-allocs">
                                    <div className="record-actions">
                                        <button
                                            className="button btn-sm"
                                            onClick={() => handleReopenDay(record.id)}
                                        >
                                            Tag wieder öffnen
                                        </button>
                                        {recAllocs.length === 0 && (
                                            <button
                                                className="button btn-sm"
                                                onClick={() => loadAllocations(record.id)}
                                            >
                                                Zuweisungen anzeigen
                                            </button>
                                        )}
                                    </div>
                                    {recAllocs.length > 0 && (
                                        <table className="table alloc-table table--cards">
                                            <thead>
                                                <tr>
                                                    <th>Projekt</th>
                                                    <th>Ticket</th>
                                                    <th>Zeit</th>
                                                    <th>Einsatzort</th>
                                                    <th>Aktivität</th>
                                                    <th>Kommentar</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recAllocs.map(a => (
                                                    <tr key={a.id}>
                                                        <td data-label="Projekt">{a.project_name}</td>
                                                        <td data-label="Ticket">{a.ticket_title || '—'}</td>
                                                        <td data-label="Zeit">{fmtMinutes(a.allocated_minutes)}</td>
                                                        <td data-label="Einsatzort">{locationLabel(a.location)}</td>
                                                        <td data-label="Aktivität">{a.activity || '—'}</td>
                                                        <td className="table-card-actions">{a.comment || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}

                            {record.status === 'open' && isExpanded && (
                                <div className="allocation-panel">
                                    <div className="balance-bar">
                                        <div className="balance-stats">
                                            <div className="balance-stat">
                                                <span className="balance-stat-label">Gesamt</span>
                                                <span className="balance-stat-value">{fmtMinutes(record.net_work_minutes)}</span>
                                            </div>
                                            <div className="balance-stat">
                                                <span className="balance-stat-label">Verteilt</span>
                                                <span className="balance-stat-value">{fmtMinutes(allocatedMin)}</span>
                                            </div>
                                            <div className={`balance-stat ${remainingMin === 0 ? 'balance-ok' : remainingMin < 0 ? 'balance-over' : 'balance-remaining'}`}>
                                                <span className="balance-stat-label">Noch zu verteilen</span>
                                                <span className="balance-stat-value">
                                                    {remainingMin < 0 ? fmtMinutes(remainingMin) + ' (zu viel)' : fmtMinutes(remainingMin)}
                                                </span>
                                            </div>
                                            {record.travel_minutes > 0 && (
                                                <div className={`balance-stat ${allocTravelMin === record.travel_minutes ? 'balance-ok' : 'balance-remaining'}`}>
                                                    <span className="balance-stat-label">Fahrtzeit</span>
                                                    <span className="balance-stat-value">{fmtMinutes(allocTravelMin)} / {fmtMinutes(record.travel_minutes)}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="balance-progress-track">
                                            <div
                                                className={`balance-progress-fill ${remainingMin === 0 ? 'fill-done' : remainingMin < 0 ? 'fill-over' : ''}`}
                                                style={{ width: `${Math.min(100, record.net_work_minutes > 0 ? Math.round(allocatedMin / record.net_work_minutes * 100) : 0)}%` }}
                                            />
                                        </div>
                                    </div>

                                    {recAllocs.length > 0 && (
                                        <table className="table alloc-table table--cards">
                                            <thead>
                                                <tr>
                                                    <th>Projekt</th>
                                                    <th>Ticket</th>
                                                    <th>Zeit</th>
                                                    <th>Fahrt (min)</th>
                                                    <th>Einsatzort</th>
                                                    <th>Aktivität</th>
                                                    <th>Kommentar</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recAllocs.map(a => editingAllocId === a.id ? (
                                                    <tr key={a.id} className="alloc-edit-row">
                                                        <td data-label="Projekt">{a.project_name}</td>
                                                        <td data-label="Ticket">
                                                            <select
                                                                className="alloc-edit-input"
                                                                value={editAllocForm.ticket_id}
                                                                onChange={e => setEditAllocForm(p => ({ ...p, ticket_id: e.target.value }))}
                                                            >
                                                                <option value="">Kein Ticket</option>
                                                                {(ticketsByProject[a.project_id] || [])
                                                                    // Done tickets are hidden, except the one already booked here —
                                                                    // otherwise saving would silently drop it.
                                                                    .filter(t => !t.is_done || String(t.id) === String(a.ticket_id))
                                                                    .map(t => (
                                                                        <option key={t.id} value={t.id}>
                                                                            {t.title}{t.is_done ? ' (abgeschlossen)' : ''}
                                                                        </option>
                                                                    ))
                                                                }
                                                            </select>
                                                        </td>
                                                        <td data-label="Zeit">
                                                            <input
                                                                className="alloc-edit-input"
                                                                type="time"
                                                                value={editAllocForm.allocated_time}
                                                                onChange={e => setEditAllocForm(p => ({ ...p, allocated_time: e.target.value }))}
                                                            />
                                                        </td>
                                                        <td data-label="Fahrt (min)">
                                                            <input
                                                                className="alloc-edit-input alloc-edit-input--narrow"
                                                                type="number"
                                                                min="0"
                                                                value={editAllocForm.travel_minutes}
                                                                onChange={e => setEditAllocForm(p => ({ ...p, travel_minutes: e.target.value }))}
                                                            />
                                                        </td>
                                                        <td data-label="Einsatzort">
                                                            <select
                                                                className="alloc-edit-input"
                                                                value={editAllocForm.location}
                                                                onChange={e => setEditAllocForm(p => ({ ...p, location: e.target.value }))}
                                                            >
                                                                <option value="">—</option>
                                                                {LOCATION_OPTIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                                                            </select>
                                                        </td>
                                                        <td data-label="Aktivität">
                                                            <input
                                                                className="alloc-edit-input"
                                                                type="text"
                                                                value={editAllocForm.activity}
                                                                onChange={e => setEditAllocForm(p => ({ ...p, activity: e.target.value }))}
                                                            />
                                                        </td>
                                                        <td className="table-card-actions">
                                                            <input
                                                                className="alloc-edit-input"
                                                                type="text"
                                                                value={editAllocForm.comment}
                                                                onChange={e => setEditAllocForm(p => ({ ...p, comment: e.target.value }))}
                                                            />
                                                        </td>
                                                        <td className="alloc-edit-actions table-card-actions">
                                                            <button className="button btn-sm" onClick={() => handleSaveEditAlloc(a.id, record.id)}>✓</button>
                                                            <button className="button btn-sm" onClick={handleCancelEditAlloc}>✕</button>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <tr key={a.id}>
                                                        <td>{a.project_name}</td>
                                                        <td>{a.ticket_title || '—'}</td>
                                                        <td>{fmtMinutes(a.allocated_minutes)}</td>
                                                        <td>{a.travel_minutes > 0 ? `${a.travel_minutes} min` : '—'}</td>
                                                        <td>{locationLabel(a.location)}</td>
                                                        <td>{a.activity || '—'}</td>
                                                        <td>{a.comment || '—'}</td>
                                                        <td className="alloc-edit-actions table-card-actions">
                                                            <button
                                                                className="button btn-sm"
                                                                onClick={() => handleStartEditAlloc(a)}
                                                            >
                                                                Bearbeiten
                                                            </button>
                                                            <button
                                                                className="button button-danger btn-sm"
                                                                onClick={() => handleDeleteAllocation(a.id, record.id)}
                                                            >
                                                                &times;
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    <div className="add-alloc-form">
                                        <h4>Stunden zuweisen</h4>
                                        <div className="form-row">
                                            <select
                                                value={allocForm.project_id}
                                                onChange={e => {
                                                    const pid = e.target.value;
                                                    setAllocForm(p => ({ ...p, project_id: pid, ticket_id: '' }));
                                                    if (pid) loadTicketsForProject(pid);
                                                }}
                                            >
                                                <option value="">Projekt wählen&hellip;</option>
                                                {projects.map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </select>
                                            {allocForm.project_id && (
                                                <select
                                                    value={allocForm.ticket_id}
                                                    onChange={e => setAllocForm(p => ({ ...p, ticket_id: e.target.value }))}
                                                >
                                                    <option value="">Kein Ticket</option>
                                                    {(ticketsByProject[allocForm.project_id] || [])
                                                        .filter(t => !t.is_done)
                                                        .map(t => (
                                                            <option key={t.id} value={t.id}>{t.title}</option>
                                                        ))
                                                    }
                                                </select>
                                            )}
                                            <label>
                                                Zeit
                                                <input
                                                    type="time"
                                                    value={allocForm.allocated_time}
                                                    onChange={e => setAllocForm(p => ({ ...p, allocated_time: e.target.value }))}
                                                />
                                            </label>
                                            <label>
                                                Fahrt (min)
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={allocForm.travel_minutes}
                                                    onChange={e => setAllocForm(p => ({ ...p, travel_minutes: e.target.value }))}
                                                />
                                            </label>
                                            <label>
                                                Einsatzort
                                                <select
                                                    value={allocForm.location}
                                                    onChange={e => setAllocForm(p => ({ ...p, location: e.target.value }))}
                                                >
                                                    <option value="">— optional —</option>
                                                    {LOCATION_OPTIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                                                </select>
                                            </label>
                                            <label>
                                                Aktivität
                                                <input
                                                    type="text"
                                                    placeholder="Aktivität (optional)"
                                                    value={allocForm.activity}
                                                    onChange={e => setAllocForm(p => ({ ...p, activity: e.target.value }))}
                                                />
                                            </label>
                                            <label>
                                                Kommentar
                                                <input
                                                    type="text"
                                                    placeholder="Kommentar (optional)"
                                                    value={allocForm.comment}
                                                    onChange={e => setAllocForm(p => ({ ...p, comment: e.target.value }))}
                                                />
                                            </label>
                                            <button
                                                className="button"
                                                onClick={() => handleAddAllocation(record)}
                                            >
                                                Hinzufügen
                                            </button>
                                        </div>
                                        {allocError[record.id] && (
                                            <p className="error-text">{allocError[record.id]}</p>
                                        )}
                                    </div>

                                    <div className="close-day-section">
                                        <button
                                            className={`button close-day-btn ${remainingMin <= 0 ? '' : 'button-disabled'}`}
                                            disabled={remainingMin > 0}
                                            onClick={() => handleCloseDay(record.id)}
                                        >
                                            Tag abschließen
                                        </button>
                                        {closeError[record.id] && (
                                            <p className="error-text">{closeError[record.id]}</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
                {filteredRecords.length > PAGE_SIZE && (
                    <button
                        className="button"
                        style={{ alignSelf: 'center', marginTop: 'var(--space-2)' }}
                        onClick={() => setShowAllRecords(v => !v)}
                    >
                        {showAllRecords
                            ? 'Weniger anzeigen'
                            : `Alle anzeigen (${filteredRecords.length})`}
                    </button>
                )}
            </div>
        </div>
    );
}
